/**
 * IntegrationsPage — settings surface for connecting external systems.
 *
 * v1 ships exactly one card: Google Search Console. The page is
 * structured so adding more (Ahrefs, Semrush, Moz, etc.) is just
 * appending another card component.
 *
 * The GSC card has three states, driven by the /api/intel/gsc/status
 * response:
 *
 *   - "Not connected"  → big "Connect Google Search Console" button.
 *                        Opens the OAuth popup; listens for the
 *                        postMessage; on success refreshes status.
 *   - "Connected, no   → property dropdown loaded from
 *      property"         /api/intel/gsc/properties. Customer picks
 *                        one and we POST it to /api/intel/gsc/property.
 *   - "Connected, with → status block + "Refresh keywords from GSC"
 *      property"         button (opens the import modal).
 *
 * The card surfaces last_synced_at + last_sync_error so the customer
 * always knows whether the integration is healthy.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import {
  MdLink, MdCheckCircle, MdRefresh, MdLogout, MdError,
  MdOpenInNew, MdSettings, MdDownload, MdTravelExplore,
} from 'react-icons/md';

import {
  getGscStatus, listGscProperties, setGscProperty, disconnectGsc,
  getGscAuthUrl,
} from '../../../api/gscRequests';
import {
  getBwtStatus, listBwtSites, setBwtSite, disconnectBwt,
  getBwtAuthUrl, importBwtBacklinks,
} from '../../../api/bwtRequests';
import GscImportModal from '../seo/components/GscImportModal';

// ---------------- styled ----------------

const Page = styled.div`
  max-width: 920px;
  margin: 0 auto;
  padding: 1.75rem 1.25rem 4rem;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  color: #0f172a;
`;
const PageHead = styled.header`
  margin-bottom: 1.5rem;
  h1 { margin: 0 0 .35rem; font-size: 1.55rem; }
  p  { margin: 0; color: #64748b; line-height: 1.55; }
`;
const Card = styled.section`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1.4rem 1.5rem 1.5rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, .04);
  & + & { margin-top: 1.25rem; }
`;
const CardHead = styled.div`
  display: flex; align-items: center; gap: .75rem;
  margin-bottom: 1rem;
  .logo {
    width: 38px; height: 38px;
    border-radius: 10px;
    display: grid; place-items: center;
    background: #eef2ff; color: #4f46e5;
    font-size: 1.45rem;
  }
  h2 { margin: 0; font-size: 1.05rem; }
  .sub { margin: .15rem 0 0; font-size: .8rem; color: #64748b; }
`;
const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  align-items: center;
  padding: .9rem 0;
  border-top: 1px solid #f1f5f9;
  &:first-of-type { border-top: 0; padding-top: 0; }
  .lbl   { font-size: .82rem; color: #64748b; margin-bottom: .15rem; }
  .val   { font-size: .94rem; color: #0f172a; word-break: break-word; }
  .val.err { color: #b91c1c; font-size: .85rem; }
`;
const Btn = styled.button`
  border: 1px solid ${p => p.$primary ? '#4f46e5' : '#cbd5e1'};
  background: ${p => p.$primary ? '#4f46e5' : '#fff'};
  color: ${p => p.$primary ? '#fff' : '#0f172a'};
  padding: .55rem 1rem;
  font-size: .88rem; font-weight: 600;
  border-radius: 9px;
  cursor: pointer;
  display: inline-flex; align-items: center; gap: .4rem;
  &:hover { filter: brightness(.95); }
  &:disabled { opacity: .55; cursor: not-allowed; }
`;
const ChipOk = styled.span`
  display: inline-flex; align-items: center; gap: .3rem;
  background: #ecfdf5; color: #047857;
  border: 1px solid #a7f3d0;
  padding: .15rem .55rem; border-radius: 999px;
  font-size: .75rem; font-weight: 600;
`;
const ChipErr = styled(ChipOk)`
  background: #fef2f2; color: #b91c1c; border-color: #fecaca;
`;
const Select = styled.select`
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 9px;
  padding: .55rem .65rem;
  font-size: .9rem;
  background: #fff;
`;
const Note = styled.div`
  margin-top: .75rem;
  font-size: .8rem; color: #64748b;
  background: #f8fafc; border: 1px dashed #cbd5e1;
  padding: .65rem .8rem; border-radius: 8px;
  line-height: 1.5;
`;

// ---------------- helpers ----------------

const formatRelative = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  const dSec = Math.max(1, Math.round((Date.now() - t) / 1000));
  if (dSec < 60)    return `${dSec}s ago`;
  if (dSec < 3600)  return `${Math.round(dSec / 60)}m ago`;
  if (dSec < 86400) return `${Math.round(dSec / 3600)}h ago`;
  return `${Math.round(dSec / 86400)}d ago`;
};

const POPUP_FEATURES = 'width=600,height=720,resizable=yes,scrollbars=yes';

// ---------------- main ----------------

export default function IntegrationsPage() {
  const [conn, setConn]   = useState(null);   // /gsc/status payload
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]   = useState(null);   // string label for current op
  const [error, setError] = useState(null);
  const [props, setProps] = useState(null);   // loaded property list
  const [showImport, setShowImport] = useState(false);

  const refreshStatus = useCallback(async () => {
    try {
      const r = await getGscStatus();
      setConn(r?.data?.connection || null);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  // listen for the popup's postMessage so we know when to refetch.
  useEffect(() => {
    const onMsg = (ev) => {
      if (ev.origin !== window.location.origin) return;
      const m = ev.data;
      if (!m || m.source !== 'gsc-oauth') return;
      if (m.success) {
        refreshStatus();
        // Auto-load properties so the user can pick one immediately.
        setBusy(null);
      } else {
        // Targeted message for the most common failure: user clicked
        // Continue without ticking the Search Console checkbox on
        // Google's granular-consent screen.
        if (m.error === 'missing_required_scope') {
          setError(
            m.message
            || "Search Console access wasn't granted. On Google's "
               + "consent screen, please tick the \"View Search "
               + "Console data\" checkbox before clicking Continue, "
               + "then retry."
          );
        } else {
          setError(m.message || m.error || 'OAuth flow was cancelled');
        }
        setBusy(null);
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [refreshStatus]);

  const handleConnect = async () => {
    setError(null);
    setBusy('opening popup');
    try {
      const r = await getGscAuthUrl({
        redirect_uri: `${window.location.origin}/auth/gsc/callback`,
      });
      const url = r?.data?.url;
      if (!url) throw new Error('no auth url returned');
      const popup = window.open(url, 'gsc-oauth', POPUP_FEATURES);
      if (!popup) {
        setError('Popup was blocked. Allow popups for app.clientcues.com and try again.');
        setBusy(null);
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
      setBusy(null);
    }
  };

  const handleLoadProperties = async () => {
    setError(null);
    setBusy('loading properties');
    try {
      const r = await listGscProperties();
      setProps(r?.data?.properties || []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setBusy(null);
    }
  };

  const handlePickProperty = async (propUrl) => {
    setBusy('saving property');
    try {
      const r = await setGscProperty(propUrl);
      setConn(r?.data?.connection || conn);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setBusy(null);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Google Search Console? Your imported keywords stay; only the connection is removed.')) return;
    setBusy('disconnecting');
    try {
      await disconnectGsc();
      setConn(null);
      setProps(null);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setBusy(null);
    }
  };

  // ---------------- render ----------------
  const isConnected = !!conn;
  const hasProperty = !!conn?.connected_property;

  return (
    <Page>
      <PageHead>
        <h1>Integrations</h1>
        <p>Connect external sources of SEO truth so they can enrich your tracked keywords, questions, and backlinks.</p>
      </PageHead>

      <Card>
        <CardHead>
          <div className="logo"><MdLink /></div>
          <div>
            <h2>Google Search Console</h2>
            <div className="sub">
              Pull the search queries Google is already showing your site for, and import them as tracked keywords.
            </div>
          </div>
          <div style={{ flex: 1 }} />
          {loading ? null
            : isConnected
              ? <ChipOk><MdCheckCircle /> Connected</ChipOk>
              : <ChipErr><MdError /> Not connected</ChipErr>}
        </CardHead>

        {loading && <div style={{ color: '#64748b', fontSize: '.9rem' }}>Loading…</div>}

        {!loading && !isConnected && (
          <>
            <Row>
              <div>
                <div className="lbl">Status</div>
                <div className="val">No Google account connected.</div>
              </div>
              <Btn $primary onClick={handleConnect} disabled={busy === 'opening popup'}>
                <MdLink /> Connect Google account
              </Btn>
            </Row>
            <Note>
              We request <strong>read-only</strong> access to Search Console
              (<code>webmasters.readonly</code>). You can revoke us any
              time from your Google Account → Security → Third-party
              access.
            </Note>
            <Note style={{
              background: '#fef3c7', border: '1px solid #fde68a',
              color: '#92400e', marginTop: '.5rem',
            }}>
              <strong>On the next screen:</strong> Google now requires
              you to manually <strong>tick the checkbox</strong> next to
              <em> "View Search Console data for your verified sites"</em>
              before clicking <strong>Continue</strong>. If you skip
              that tick, the connection won't work and you'll need to
              start over.
            </Note>
          </>
        )}

        {!loading && isConnected && (
          <>
            <Row>
              <div>
                <div className="lbl">Connected Google account</div>
                <div className="val">{conn.connected_email || 'unknown'}</div>
              </div>
              <Btn onClick={handleDisconnect} disabled={busy === 'disconnecting'}>
                <MdLogout /> Disconnect
              </Btn>
            </Row>

            <Row>
              <div>
                <div className="lbl">Tracked property</div>
                <div className="val">
                  {hasProperty ? conn.connected_property : <em style={{ color: '#b45309' }}>none selected</em>}
                </div>
              </div>
              <Btn onClick={handleLoadProperties} disabled={busy === 'loading properties'}>
                <MdSettings /> {hasProperty ? 'Change property' : 'Choose property'}
              </Btn>
            </Row>

            {props && (
              <Row style={{ display: 'block' }}>
                <div className="lbl" style={{ marginBottom: '.4rem' }}>
                  Pick which property to track ({props.length} available)
                </div>
                {props.length === 0 ? (
                  <Note>
                    Your Google account doesn't currently have access to
                    any verified properties. Verify a site at{' '}
                    <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer">
                      search.google.com/search-console <MdOpenInNew />
                    </a>{' '}
                    and reload here.
                  </Note>
                ) : (
                  <Select
                    value={conn.connected_property || ''}
                    onChange={(e) => handlePickProperty(e.target.value)}
                    disabled={busy === 'saving property'}
                  >
                    <option value="" disabled>— pick one —</option>
                    {props.map(p => (
                      <option key={p.property} value={p.property}>
                        {p.property} ({p.permission})
                      </option>
                    ))}
                  </Select>
                )}
              </Row>
            )}

            <Row>
              <div>
                <div className="lbl">Last sync</div>
                <div className="val">
                  {conn.last_synced_at
                    ? `${formatRelative(conn.last_synced_at)} · +${conn.last_sync_keywords_added || 0} added · ${conn.last_sync_keywords_updated || 0} enriched`
                    : 'never'}
                </div>
                {conn.last_sync_error && (
                  <div className="val err">Error: {conn.last_sync_error}</div>
                )}
              </div>
              <Btn
                $primary
                disabled={!hasProperty || busy === 'importing'}
                onClick={() => setShowImport(true)}
                title={hasProperty ? 'Pull top queries from GSC' : 'Pick a property first'}
              >
                <MdDownload /> Import keywords
              </Btn>
            </Row>

            <Note>
              Imports are <strong>manual</strong> in v1 — click "Import
              keywords" whenever you want fresh numbers. Existing
              keywords are <em>enriched in place</em>; new queries are
              added to the Keywords page with source = <code>gsc</code>.
            </Note>

            {showImport && (
              <GscImportModal
                onClose={() => setShowImport(false)}
                onDone={(r) => {
                  setShowImport(false);
                  refreshStatus();
                  setBusy(null);
                  if (r) setError(null);
                }}
              />
            )}
          </>
        )}

        {error && <ChipErr style={{ marginTop: '.75rem' }}><MdError /> {error}</ChipErr>}
      </Card>

      <BwtCard />

      <Card>
        <CardHead>
          <div className="logo" style={{ background: '#fef3c7', color: '#a16207' }}>
            <MdRefresh />
          </div>
          <div>
            <h2>More integrations coming soon</h2>
            <div className="sub">
              Ahrefs, Semrush, and Moz for competitor backlink data + domain ratings.
            </div>
          </div>
        </CardHead>
        <Note>
          Want a specific integration sooner? Tell us at{' '}
          <a href="mailto:product@clientcues.com">product@clientcues.com</a>.
        </Note>
      </Card>
    </Page>
  );
}

// ---------------- Bing Webmaster card ----------------
// Self-contained: own state + handlers, reuses the shared styled above.
// Connect -> pick verified site -> import inbound links (kind='self'),
// which grounds seo.referring_domains and fills "Our backlinks".
function BwtCard() {
  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [sites, setSites] = useState(null);
  const [result, setResult] = useState(null);
  const autoSiteTried = useRef(false);

  const refreshStatus = useCallback(async () => {
    try {
      const r = await getBwtStatus();
      setConn(r?.data?.connection || null);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  // Auto-select the first verified site when connected but none picked yet
  // (once per mount). The customer can still switch via "Change site".
  useEffect(() => {
    if (loading || !conn || conn.connected_site || autoSiteTried.current) return;
    autoSiteTried.current = true;
    (async () => {
      try {
        const r = await listBwtSites();
        const list = r?.data?.sites || [];
        if (list.length > 0) {
          const s = await setBwtSite(list[0].site_url);
          setConn(s?.data?.connection || null);
          setError(null);
        } else {
          setSites([]); // no verified sites → show the picker note
        }
      } catch (e) {
        setError(e?.response?.data?.error || e.message);
      }
    })();
  }, [conn, loading]);

  useEffect(() => {
    const onMsg = (ev) => {
      if (ev.origin !== window.location.origin) return;
      const m = ev.data;
      if (!m || m.source !== 'bwt-oauth') return;
      if (m.success) { refreshStatus(); setBusy(null); }
      else { setError(m.message || m.error || 'OAuth flow was cancelled'); setBusy(null); }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [refreshStatus]);

  const handleConnect = async () => {
    setError(null); setBusy('opening popup');
    try {
      const r = await getBwtAuthUrl({ redirect_uri: `${window.location.origin}/auth/bing/callback` });
      const url = r?.data?.url;
      if (!url) throw new Error('no auth url returned');
      const popup = window.open(url, 'bwt-oauth', POPUP_FEATURES);
      if (!popup) { setError('Popup was blocked. Allow popups for app.clientcues.com and try again.'); setBusy(null); }
    } catch (e) { setError(e?.response?.data?.error || e.message); setBusy(null); }
  };

  const handleLoadSites = async () => {
    setError(null); setBusy('loading sites');
    try {
      const r = await listBwtSites();
      setSites(r?.data?.sites || []);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setBusy(null); }
  };

  const handlePickSite = async (siteUrl) => {
    setBusy('saving site');
    try {
      const r = await setBwtSite(siteUrl);
      setConn(r?.data?.connection || conn);
      setError(null);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setBusy(null); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Bing Webmaster? Imported backlinks stay; only the connection is removed.')) return;
    setBusy('disconnecting');
    try {
      await disconnectBwt();
      setConn(null); setSites(null); setResult(null); setError(null);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setBusy(null); }
  };

  const handleImport = async () => {
    setBusy('importing'); setError(null); setResult(null);
    try {
      const r = await importBwtBacklinks({ max_pages: 20 });
      setResult(r?.data || null);
      refreshStatus();
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setBusy(null); }
  };

  const isConnected = !!conn;
  const hasSite = !!conn?.connected_site;

  return (
    <Card>
      <CardHead>
        <div className="logo" style={{ background: '#e0f2fe', color: '#0369a1' }}><MdTravelExplore /></div>
        <div>
          <h2>Bing Webmaster Tools</h2>
          <div className="sub">
            Pull your Bing data — inbound links, search queries, traffic &amp; crawl health — to ground your SEO coverage and drive action items.
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {loading ? null
          : isConnected
            ? <ChipOk><MdCheckCircle /> Connected</ChipOk>
            : <ChipErr><MdError /> Not connected</ChipErr>}
      </CardHead>

      {loading && <div style={{ color: '#64748b', fontSize: '.9rem' }}>Loading…</div>}

      {!loading && !isConnected && (
        <>
          <Row>
            <div>
              <div className="lbl">Status</div>
              <div className="val">No Bing account connected.</div>
            </div>
            <Btn $primary onClick={handleConnect} disabled={busy === 'opening popup'}>
              <MdLink /> Connect Bing account
            </Btn>
          </Row>
          <Note>
            We request <strong>read-only</strong> access (<code>Webmaster.read</code>).
            You'll sign in on Bing (a Microsoft, Google, or Facebook login works) and
            can revoke us any time from your Bing Webmaster account settings.
          </Note>
        </>
      )}

      {!loading && isConnected && (
        <>
          <Row>
            <div>
              <div className="lbl">Connected account</div>
              <div className="val">{conn.connected_email || 'Bing Webmaster'}</div>
            </div>
            <Btn onClick={handleDisconnect} disabled={busy === 'disconnecting'}>
              <MdLogout /> Disconnect
            </Btn>
          </Row>

          <Row>
            <div>
              <div className="lbl">Tracked site</div>
              <div className="val">
                {hasSite ? conn.connected_site : <em style={{ color: '#b45309' }}>none selected</em>}
              </div>
            </div>
            <Btn onClick={handleLoadSites} disabled={busy === 'loading sites'}>
              <MdSettings /> {hasSite ? 'Change site' : 'Choose site'}
            </Btn>
          </Row>

          {sites && (
            <Row style={{ display: 'block' }}>
              <div className="lbl" style={{ marginBottom: '.4rem' }}>
                Pick which verified site to track ({sites.length} available)
              </div>
              {sites.length === 0 ? (
                <Note>
                  Your Bing account has no verified sites. Verify one at{' '}
                  <a href="https://www.bing.com/webmasters" target="_blank" rel="noopener noreferrer">
                    bing.com/webmasters <MdOpenInNew />
                  </a>{' '}and reload here.
                </Note>
              ) : (
                <Select
                  value={conn.connected_site || ''}
                  onChange={(e) => handlePickSite(e.target.value)}
                  disabled={busy === 'saving site'}
                >
                  <option value="" disabled>— pick one —</option>
                  {sites.map((s) => (
                    <option key={s.site_url} value={s.site_url}>{s.site_url}</option>
                  ))}
                </Select>
              )}
            </Row>
          )}

          <Row>
            <div>
              <div className="lbl">Last sync</div>
              <div className="val">
                {conn.last_synced_at
                  ? `${formatRelative(conn.last_synced_at)} · +${conn.last_sync_backlinks_added || 0} added · ${conn.last_sync_backlinks_updated || 0} updated`
                  : 'never'}
              </div>
              {conn.last_sync_error && (
                <div className="val err">Error: {conn.last_sync_error}</div>
              )}
              {result && (
                <div className="val" style={{ color: '#047857', fontSize: '.85rem' }}>
                  {(result.backlinks?.referring_domains ?? 0)} referring domains ·{' '}
                  {(result.queries?.total ?? 0)} Bing queries ·{' '}
                  {(result.crawl?.crawled_pages ?? 0)} pages crawled ({(result.crawl?.errors ?? 0)} errors) ·{' '}
                  {(result.traffic?.impressions ?? 0)} impressions/30d
                </div>
              )}
            </div>
            <Btn
              $primary
              disabled={!hasSite || busy === 'importing'}
              onClick={handleImport}
              title={hasSite ? 'Pull inbound links from Bing' : 'Pick a site first'}
            >
              <MdDownload /> {busy === 'importing' ? 'Importing…' : 'Import backlinks'}
            </Btn>
          </Row>

          <Note>
            Runs <strong>automatically every week</strong> (Sunday); use <strong>Import</strong>
            for a refresh now. Backlinks + search queries land on your Keywords/Backlinks,
            and traffic + crawl health ground signals on your SEO aspect — which feed its
            score, coverage, and action items.
          </Note>
        </>
      )}

      {error && <ChipErr style={{ marginTop: '.75rem' }}><MdError /> {error}</ChipErr>}
    </Card>
  );
}
